import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import {
  School,
  FileText,
  Settings,
  Mail,
  Upload,
  CheckCircle,
  AlertCircle,
  Globe,
  Phone,
  MapPin,
  Save,
  Shield,
  Clock,
  Camera,
  Calendar,
  MessageSquare,
  Plus,
  Edit,
  Trash2,
  Eye,
  CreditCard,
  Users,
  BookOpen,
  Image,
  Fingerprint,
  Lock,
  Network,
  ShieldCheck,
  GraduationCap, ArrowLeft,
  Percent,
  Hash,
  BellRing
} from 'lucide-react';
import SchoolSettingsSection from './SchoolSettingsSection';
import LicenceSection from './LicenceSection';
import EventsSettingsSection from './EventsSettingsSection';
import CommunicationsSettingsSection from './CommunicationsSettingsSection';
import SalaryConfigurationSection from './SalaryConfigurationSection';
import SessionConfigDashboard from './SessionConfigDashboard';
import ExamenSettingsPage from './ExamenSettingsPage';
import DepensesFixesSettingsPage from './DepensesFixesSettingsPage';
import ParametresClasseMatiere from './ParametresClasseMatiere';
import ParametresActualites from './ParametresActualites';
import useEventsParams from '../hooks/useEventsParams';
import useCommunicationsParams from '../hooks/useCommunicationsParams';
import useActualitesParams from '../hooks/useActualitesParams'; // ✅ NOUVEAU
import ConfigurationCalendrierAnnuel from './ConfigurationCalendrierAnnuel';
import ParametresPointage from './ParametresPointage';
import { useFeatures } from '../contexts/FeaturesContext';
import ParametrageCarnetEvaluation from './ParametrageCarnetEvaluation';
import ParametresOrganigramme from './ParametresOrganigramme';
import ParametresSalaires from './ParametresSalaires';
import ParametresScolarite from './ParametresScolarite';
import ParametresNumerotation from './ParametresNumerotation';
import PhotosEleves from './parametrage/PhotosEleves';
import ParametresAlertesCritiques from './ParametresAlertesCritiques';

const ParametrageApp = () => {
  const { estActive } = useFeatures();

  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedSection, setSelectedSection] = useState(null);

  const { events } = useEventsParams();
  const { communications } = useCommunicationsParams();
  const { actualites } = useActualitesParams(); // ✅ NOUVEAU

  // États pour les informations de licence
  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedSection(null);
  };

  const handleSectionClick = (sectionName, sectionId, isActive) => {
    if (isActive) {
      setCurrentView(sectionId);
      setSelectedSection({ name: sectionName, id: sectionId });
    }
  };

  // === PAGE EN CONSTRUCTION ===
  const PageEnConstruction = ({ titre, description, icon: IconComponent }) => (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Button
            onClick={handleBackToDashboard}
            variant="outline"
            size="sm"
            className="border-[#246990] text-[#246990] hover:bg-[#246990] hover:text-white"
          >
            <ArrowLeft size={15} className="mr-1.5" /> Retour
          </Button>
          <div className="w-8 h-8 bg-[#246990] rounded-lg flex items-center justify-center">
            <IconComponent className="text-white" size={16} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{titre}</h1>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-[#246990]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Settings className="w-10 h-10 text-[#246990]" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Section en développement</h3>
            <p className="text-gray-600 mb-6">
              Cette section de paramétrage sera bientôt disponible pour une configuration avancée.
            </p>
            <Button
              onClick={handleBackToDashboard}
              className="bg-[#246990] hover:bg-[#1a4d6e] text-white"
            >
              Retour au Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // === RENDU CONDITIONNEL POUR CHAQUE SECTION ===

  // Section Établissement
  if (currentView === 'etablissement') {
    return <SchoolSettingsSection onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Licence
  if (currentView === 'licence') {
    return <LicenceSection onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Évènements
  if (currentView === 'evenements') {
    return <EventsSettingsSection onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Communications
  if (currentView === 'communications') {
    return <CommunicationsSettingsSection onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Gestion Comptable
  if (currentView === 'gestion-comptable') {
    return <SalaryConfigurationSection onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Examens nationaux
  //
  // Le paramétrage vivait dans le module Examens lui-même, sous un onglet
  // « Paramètres ». Sa place est ici, avec les réglages des autres modules.
  if (currentView === 'examens-nationaux') {
    return <ExamenSettingsPage onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Dépenses fixes récurrentes
  //
  // Le modèle — loyer, électricité, abonnement — est un objet d'école : il
  // apparaît dans le portefeuille de chaque gestionnaire. Sa gestion vivait
  // pourtant dans le portefeuille, à côté de son application. Le modèle vient
  // ici ; l'application, qui sort de l'argent, reste à la caisse.
  if (currentView === 'depenses-fixes') {
    return <DepensesFixesSettingsPage onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Configuration des Sessions
  if (currentView === 'configuration-sessions') {
    return <SessionConfigDashboard onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Classes & Matières
  if (currentView === 'classes-matieres') {
    return <ParametresClasseMatiere onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Calendrier Annuel
  if (currentView === 'calendrier-annuel') {
    return <ConfigurationCalendrierAnnuel onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Actualités
  //
  // La page etait un modal rendu avec isOpen={true} sous cet en-tete : deux
  // en-tetes empiles, un voile noir plein ecran sur une page qui n'en est pas
  // une, et un bouton « Fermer » en pied alors que le retour etait deja en
  // haut. C'est maintenant une page comme les autres.
  if (currentView === 'actualites') {
    return <ParametresActualites onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Organigramme
  if (currentView === 'organigramme') {
    return <ParametresOrganigramme onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Gestion des Pointages
  if (currentView === 'pointages') {
    return <ParametresPointage onBackToDashboard={handleBackToDashboard} />;
  }

  if (currentView === 'parametres-salaires') {
    return <ParametresSalaires onBackToDashboard={handleBackToDashboard} />;
  }

  if (currentView === 'carnet-evaluation') return <ParametrageCarnetEvaluation onBackToDashboard={() => setCurrentView(null)} />;

  // Section Scolarité et réductions
  if (currentView === 'scolarite') {
    return <ParametresScolarite onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Identifiants et matricules — au socle de toutes les licences
  if (currentView === 'numerotation') {
    return <ParametresNumerotation onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Photos des élèves — avancement de la campagne photo, classe par
  // classe. Page en LECTURE SEULE : une photo se pose depuis la fiche de
  // l'élève, ou arrive de l'application des parents. Jamais depuis ici.
  if (currentView === 'photos-eleves') {
    return <PhotosEleves onBackToDashboard={handleBackToDashboard} />;
  }

  // Section Alertes critiques : délai de réouverture et sons du panneau,
  // détecteurs d'anomalies, seuils de la règle des montants. Le serveur ne
  // renvoie que les détecteurs ouverts par la licence de l'école, et dit si
  // l'agent peut modifier (sinon la page passe en lecture seule).
  if (currentView === 'alertes-critiques') {
    return <ParametresAlertesCritiques onBackToDashboard={handleBackToDashboard} />;
  }

  // « Paramètres généraux » et « Paramètres de communication » affichaient
  // « Section en développement » depuis toujours. Une carte qui ne mène nulle
  // part apprend à ne plus cliquer : elles sont retirées plutôt que laissées
  // en promesse.

  // === DASHBOARD PRINCIPAL ===
  const parametrageSections = [
    {
      id: 'licence',
      name: 'Licence et version',
      description: 'Informations sur votre abonnement',
      icon: Shield,
      color: 'blue',
      isActive: true
    },
    {
      id: 'etablissement',
      name: 'Établissement',
      description: 'Informations générales de l\'école',
      icon: School,
      color: 'blue',
      isActive: true
    },
    {
      id: 'evenements',
      name: 'Derniers Évènements',
      description: 'Évènements page d\'accueil',
      icon: Calendar,
      color: 'orange',
      isActive: true
    },
    {
      id: 'communications',
      name: 'Dernières communications',
      description: 'Messages page d\'accueil',
      icon: MessageSquare,
      color: 'purple',
      isActive: true
    },
    // ✅ NOUVELLE CARD : Actualités
    {
      id: 'actualites',
      name: 'Actualités',
      description: 'Gestion des actualités de la page d\'accueil',
      icon: Image,
      color: 'green',
      isActive: true
    },


    {
      id: 'organigramme',
      name: 'Organigramme',
      description: 'Structure RH et règlement intérieur',
      icon: Network,
      color: 'blue',
      isActive: true
    },


    {
      id: 'pointages',
      name: 'Règles de pointage',
      description: "Plages horaires, tolérances et règles d'envoi comptable",
      icon: Fingerprint, // ← AJOUTER import Fingerprint en haut
      color: 'blue',
      isActive: true
    },


    {
      id: 'carnet-evaluation',
      name: "Carnet d'évaluation",
      description: 'Déverrouillage, appréciations, champs visibles',
      icon: BookOpen,
      color: 'blue',
      isActive: true
    },

    {
      id: 'parametres-salaires',
      name: 'Controle des salaires',
      description: 'Modifications et avances salariales',
      icon: ShieldCheck,
      color: 'blue',
      isActive: true
    },
    {
      id: 'depenses-fixes',
      name: 'Dépenses fixes récurrentes',
      description: 'Charges mensuelles proposées dans les portefeuilles',
      icon: Clock,
      color: 'purple',
      isActive: true
    },
    {
      id: 'examens-nationaux',
      name: 'Gestion examens nationaux',
      description: 'Frais, dates limites et pièces exigées par campagne',
      icon: GraduationCap,
      color: 'blue',
      isActive: true
    },
    {
      id: 'configuration-sessions',
      name: 'Configuration des sessions',
      description: 'Durées et paramètres de sessions utilisateur',
      icon: Users,
      color: 'blue',
      isActive: true
    },
    {
      id: 'classes-matieres',
      name: 'Classes et matières',
      description: 'Configuration des sections et organisation pédagogique',
      icon: BookOpen,
      color: 'blue',
      isActive: true
    },
    {
      id: 'calendrier-annuel',
      name: 'Calendrier annuel',
      description: 'Configuration des dates de l\'année scolaire',
      icon: Calendar,
      color: 'orange',
      isActive: true
    },
    {
      // Ce que l'ecole facture aux familles. Ces deux reglages vivaient dans
      // « Identite de l'etablissement », a cote du logo et de l'adresse, alors
      // qu'ils bornent de l'argent. Ils ne pouvaient pas non plus rejoindre la
      // paie : ces pages sont derriere `module.paie`, et une ecole sans module
      // paie aurait perdu l'acces a ses propres reductions. Elles suivent donc
      // la comptabilite (voir LICENCE_PAR_SECTION).
      id: 'scolarite',
      name: 'Scolarité et réductions',
      description: 'Plafond des réductions et familles nombreuses',
      icon: Percent,
      color: 'teal',
      isActive: true
    },
    {
      // Au socle : toute école crée des comptes et inscrit des élèves.
      id: 'numerotation',
      name: 'Identifiants et matricules',
      description: 'Forme des identifiants de connexion et modèle des matricules',
      icon: Hash,
      color: 'blue',
      isActive: true
    },
    {
      // Au socle également : toute école a des élèves, et tout élève a un
      // dossier. Mesure du 04/10/2026 : 2 098 élèves actifs sur les trois
      // écoles réelles, et trois photos en tout. La carte mène donc à un
      // écran de CAMPAGNE — ce qui reste à faire — et non à un répertoire
      // de photos, qui tiendrait aujourd'hui en une ligne.
      //
      // VOLONTAIREMENT ABSENTE DE LICENCE_PAR_SECTION (plus bas) : aucune
      // licence ne conditionne le fait d'avoir des élèves et des dossiers.
      // Ne pas l'y ajouter par symétrie avec les cartes voisines.
      id: 'photos-eleves',
      name: 'Photos des élèves',
      description: 'Avancement de la campagne photo, classe par classe',
      icon: Camera,
      color: 'blue',
      isActive: true
    },
    {
      // Au socle (décision du 13/10/2026) : le panneau des alertes critiques
      // s'ouvre dans toute école, quelle que soit sa licence. Ce sont les
      // DÉTECTEURS qui dépendent d'un module, et le serveur les filtre
      // lui-même avant de les renvoyer à la page.
      //
      // VOLONTAIREMENT ABSENTE DE LICENCE_PAR_SECTION (plus bas) : aucune
      // licence ne conditionne le panneau lui-même, ni le réglage de son
      // rappel et de ses sons. Ne pas l'y ajouter par symétrie avec les
      // cartes voisines.
      id: 'alertes-critiques',
      name: 'Alertes critiques',
      description: "Suivi, données suspectes, réglages et détecteurs",
      icon: BellRing,
      color: 'red',
      isActive: true
    }
  ];

  // ------------------------------------------------------------------
  // Filtrage par licence
  // ------------------------------------------------------------------
  // Regler un module que l ecole n a pas souscrit n a pas de sens : la garde
  // serveur refuserait de toute facon. Les cartes concernees disparaissent
  // donc, plutot que de mener a un ecran ferme.
  //
  // Une carte absente de cette table releve du socle : etablissement, annees
  // scolaires, communications, droits — jamais filtrees.
  const LICENCE_PAR_SECTION = {
    // Les plages d'arrivee, les tolerances et les regles d'envoi comptable ne
    // servent ni au carnet manuel — qui calcule le retard sur l'horaire de la
    // classe et n'appelle jamais cette API — ni a une ecole sans badge. Elles
    // reglent le pointage automatique.
    'pointages': 'cap.rfid',

    'parametres-salaires': 'module.paie',
    'depenses-fixes': 'module.comptabilite',
    // Plafond des reductions et regles des familles nombreuses : elles bornent
    // ce que l'ecole facture. Sans comptabilite, il n'y a rien a facturer
    // (decision du 16 septembre 2026 : hors du socle).
    'scolarite': 'module.comptabilite',

    // Malgre son nom, cette carte ouvre la configuration des PLAFONDS DE PAIE :
    // prime maximale, retenue maximale, avance maximale, toutes en pourcentage
    // du salaire. Gardee sur la comptabilite, elle s'affichait a une ecole en
    // Gestion — comptabilite sans paie — qui pouvait ouvrir l'ecran et remplir
    // un formulaire que le serveur refusait ensuite, l'API `/api/variable-elements`
    // etant gardee par `module.paie`.
    'gestion-comptable': 'module.paie',

    'examens-nationaux': 'module.examens',
    'carnet-evaluation': 'module.evaluations',
    'classes-matieres': 'module.classes_matieres',

    // Structure RH et reglement interieur : sans le module, il n'y a pas
    // d'organigramme a dessiner. Sans effet au-dela de Decouverte, qui est la
    // seule offre sans les ressources humaines.
    'organigramme': 'module.rh',

    // Les durees de session se vendent a part.
    'configuration-sessions': 'cap.sessions'
  };

  const sectionsVisibles = parametrageSections.filter((section) => {
    const cle = LICENCE_PAR_SECTION[section.id];
    return !cle || estActive(cle);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Actif': return 'text-green-600';
      case 'Attention': return 'text-orange-600';
      case 'Bientôt disponible': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  const getButtonColor = (color, isActive) => {
    if (!isActive) return 'bg-gray-100 text-gray-500';

    const colors = {
      blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
      green: 'bg-green-50 text-green-700 hover:bg-green-100',
      orange: 'bg-orange-50 text-orange-700 hover:bg-orange-100',
      purple: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
      red: 'bg-red-50 text-red-700 hover:bg-red-100'
    };
    return colors[color] || 'bg-gray-50 text-gray-700 hover:bg-gray-100';
  };

  const getIconColor = (color, isActive) => {
    if (!isActive) return 'text-gray-500 bg-gray-100';

    const colors = {
      blue: 'text-blue-600 bg-blue-100',
      green: 'text-green-600 bg-green-100',
      orange: 'text-orange-600 bg-orange-100',
      purple: 'text-purple-600 bg-purple-100',
      red: 'text-red-600 bg-red-100'
    };
    return colors[color] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="w-full">
      {/* Grille des sections de paramétrage */}
      <Card className="mb-6 border-0 shadow-sm">
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sectionsVisibles.map((section) => {
              const IconComponent = section.icon;
              return (
                <Card
                  key={section.id}
                  className={`border shadow-sm cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${!section.isActive ? 'opacity-60' : ''}`}
                  onClick={() => handleSectionClick(section.name, section.id, section.isActive)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIconColor(section.color, section.isActive)}`}>
                        <IconComponent size={20} />
                      </div>
                    </div>

                    <h3 className={`font-bold mb-1 text-sm ${section.isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                      {section.name}
                    </h3>
                    <p className={`text-xs mb-3 ${section.isActive ? 'text-gray-600' : 'text-gray-500'}`}>
                      {section.description}
                    </p>

                    <Button
                      className={`w-full text-xs font-medium ${getButtonColor(section.color, section.isActive)}`}
                      size="sm"
                      disabled={!section.isActive}
                    >
                      {section.isActive ? 'Accéder' : 'Bientôt disponible'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Notification de succès */}
      {showSuccess && (
        <div className="fixed bottom-6 right-6 bg-white border border-green-200 shadow-2xl p-4 rounded-2xl flex items-center z-50">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
            <CheckCircle className="text-white" size={16} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Paramètres enregistrés</p>
            <p className="text-sm text-gray-600">Modifications appliquées avec succès</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametrageApp;
